import { RefObject, useEffect, useRef, useState } from "react";
import { useVideoControlActions } from "../contexts/VideoControlContext";

// Floor on the track width, so cursor re-renders quantize to ~one per pixel of
// travel and an unmeasured track can't drive a re-render storm.
const MIN_QUANTIZATION_WIDTH_PX = 1000;

// Drives a timeline cursor from the leader video element each animation frame
// instead of the ~4Hz React clock, whose lag is multiplied by playback rate
// (~9s at 16x). Re-renders only once the live time moves a pixel's worth of the
// window. getLiveCurrentTime supplies the seek / no-leader / live-leader
// fallbacks, so this hook inherits them.
export const useLiveCursorTime = ({
  trackRef,
  visibleDuration,
  visibleStartSeconds,
}: {
  trackRef?: RefObject<HTMLElement | null>;
  visibleDuration: number;
  visibleStartSeconds?: number;
}): number => {
  const { getLiveCurrentTime } = useVideoControlActions();
  const [liveTime, setLiveTime] = useState(() => getLiveCurrentTime());
  const liveTimeRef = useRef(liveTime);
  const visibleDurationRef = useRef(visibleDuration);
  visibleDurationRef.current = visibleDuration;
  const visibleStartRef = useRef(visibleStartSeconds);
  visibleStartRef.current = visibleStartSeconds;

  useEffect(() => {
    let frameId: number;
    const tick = () => {
      const currentVisibleDuration = visibleDurationRef.current;
      if (currentVisibleDuration > 0) {
        const visibleStart = visibleStartRef.current;
        const liveCurrentTime = getLiveCurrentTime();
        // When the window start is known, park the cursor at the window edge
        // instead of re-rendering every frame while the leader plays outside it.
        const next =
          visibleStart === undefined
            ? liveCurrentTime
            : Math.min(
                Math.max(liveCurrentTime, visibleStart),
                visibleStart + currentVisibleDuration
              );
        const trackWidthPx = Math.max(
          trackRef?.current?.offsetWidth ?? 0,
          MIN_QUANTIZATION_WIDTH_PX
        );
        const quantum = currentVisibleDuration / trackWidthPx;
        if (Math.abs(next - liveTimeRef.current) >= quantum) {
          liveTimeRef.current = next;
          setLiveTime(next);
        }
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [getLiveCurrentTime, trackRef]);

  return liveTime;
};
